import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { LayoutDashboard, Inbox, Users, LogOut, Menu, X, ShieldCheck, Star, UserCheck, Wrench, Boxes, ShoppingCart, FileText, TrendingUp, BookOpen, LifeBuoy, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StaffAppInstall } from "@/components/staff/StaffAppInstall";
import { toast } from "sonner";

const navItems = [
  { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "staff-nav-dashboard" },
  { to: "/staff/enquiries", label: "Enquiries", icon: Inbox, testId: "staff-nav-enquiries" },
  { to: "/staff/baskets", label: "Unfinished checkouts", icon: ShoppingCart, testId: "staff-nav-baskets" },
  { to: "/staff/kit-builds", label: "Kit builds", icon: Wrench, testId: "staff-nav-kit-builds" },
  { to: "/staff/payments", label: "Sandbox payments", icon: ShieldCheck, testId: "staff-nav-payments" },
  { to: "/staff/stock", label: "Stock", icon: Boxes, testId: "staff-nav-stock" },
  { to: "/staff/files", label: "Files", icon: FileText, testId: "staff-nav-files" },
  { to: "/staff/delivery", label: "Delivery", icon: Truck, testId: "staff-nav-delivery" },
  { to: "/staff/price-history", label: "Price history", icon: TrendingUp, testId: "staff-nav-price-history" },
  { to: "/staff/guides", label: "Guides", icon: BookOpen, testId: "staff-nav-guides" },
  { to: "/staff/support", label: "Support desk", icon: LifeBuoy, testId: "staff-nav-support" },
  { to: "/staff/reviews", label: "Reviews", icon: Star, testId: "staff-nav-reviews" },
  { to: "/staff/customers", label: "Accounts", icon: UserCheck, testId: "staff-nav-customers" },
  { to: "/staff/team", label: "Team", icon: Users, adminOnly: true, testId: "staff-nav-team" },
];

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    if (!await logout()) return;
    toast.success("Signed out");
    navigate("/staff/login");
  };

  const items = navItems.filter((i) => !i.adminOnly || user?.role === "admin");

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm font-display text-sm font-semibold transition-colors min-h-[44px] ${
      isActive ? "bg-ei-gold/15 text-ei-white" : "text-ei-white/65 hover:text-ei-white hover:bg-ei-white/5"
    }`;

  return (
    <div className="min-h-screen flex bg-ei-white">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-ei-ink text-ei-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-6 border-b border-ei-white/10">
          <Link to="/staff/dashboard" className="font-display text-lg font-bold text-ei-white">EI Battery</Link>
          <div className="ei-eyebrow text-ei-gold mt-1">Sales workspace</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={linkCls} onClick={() => setOpen(false)} data-testid={it.testId}>
              <it.icon className="w-5 h-5" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-ei-white/10">
          <StaffAppInstall />
          <div className="px-4 py-2">
            <div className="text-sm font-semibold text-ei-white truncate">{user?.name}</div>
            <div className="text-xs text-ei-white/50 flex items-center gap-1 mt-0.5">
              Staff
            </div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-ei-white/65 hover:text-ei-white hover:bg-ei-white/5 font-display text-sm font-semibold min-h-[44px]" data-testid="staff-logout">
            <LogOut className="w-5 h-5" /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-ei-ink text-ei-white flex items-center justify-between px-4 py-3">
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="w-11 h-11 flex items-center justify-center" data-testid="staff-menu-toggle">
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-display font-bold">EI Battery</span>
          <button onClick={doLogout} className="w-11 h-11 flex items-center justify-center" aria-label="Sign out"><LogOut className="w-5 h-5" /></button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
